import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormControlLabel from '@mui/material/FormControlLabel';
import RedoIcon from '@mui/icons-material/Redo';
import Tooltip from '@mui/material/Tooltip';
import UndoIcon from '@mui/icons-material/Undo';

import StaticAdsService from 'services/StaticAdsService';

import { GenerationRow, OverlayElement, OverlaySpec } from './types';
import { parseInlineMarkup, renderableLines } from './inlineMarkup';
import { colorWithOpacity, gradientCssAngle, inkExtentEm, renderOverlayToBlob } from './overlayCanvas';
import { loadAllOverlayFonts, loadOverlayFonts } from './overlayFonts';

// The browser IS the editor: the text-free plate renders with the overlay
// spec's elements positioned on top — drag to move, click to select, edit
// wording/size/color in the panel. The AI's layout pass supplied the starting
// spec (placement, fonts, colors); every edit here is the user's. Save sends
// the spec back and the SERVER flattens it onto the plate with the same
// renderer the worker used — the preview is approximate (browser fonts differ
// slightly), the saved image is authoritative.

// The families installed on the SERVER (which does the final render), mapped
// to the closest stacks browsers actually have so the preview approximates the
// saved image. Liberation Sans/Serif are metric twins of Arial/Times.
const FONT_OPTIONS: { value: string; label: string; preview: string }[] = [
  { value: 'Montserrat', label: 'Montserrat', preview: "Montserrat, 'Helvetica Neue', Arial, sans-serif" },
  { value: 'Poppins', label: 'Poppins', preview: "Poppins, 'Helvetica Neue', Arial, sans-serif" },
  { value: 'Oswald', label: 'Oswald (condensed)', preview: "Oswald, 'Arial Narrow', Arial, sans-serif" },
  { value: 'Bebas Neue', label: 'Bebas Neue (display caps)', preview: "'Bebas Neue', 'Arial Narrow', Arial, sans-serif" },
  { value: 'Anton', label: 'Anton (impact)', preview: "Anton, 'Arial Black', Arial, sans-serif" },
  { value: 'Playfair Display', label: 'Playfair Display (serif)', preview: "'Playfair Display', Georgia, serif" },
  { value: 'Inter', label: 'Inter', preview: "Inter, 'Helvetica Neue', Arial, sans-serif" },
  { value: 'Roboto', label: 'Roboto', preview: "Roboto, 'Helvetica Neue', Arial, sans-serif" },
  { value: 'Open Sans', label: 'Open Sans', preview: "'Open Sans', 'Segoe UI', Arial, sans-serif" },
  { value: 'Lato', label: 'Lato', preview: "Lato, 'Helvetica Neue', Arial, sans-serif" },
  { value: 'Roboto Slab', label: 'Roboto Slab', preview: "'Roboto Slab', 'Courier New', serif" },
  { value: 'DejaVu Sans', label: 'DejaVu Sans (default)', preview: "'DejaVu Sans', Verdana, sans-serif" },
  { value: 'Liberation Sans', label: 'Liberation Sans (≈ Arial)', preview: "Arial, Helvetica, sans-serif" },
  { value: 'Liberation Sans Narrow', label: 'Liberation Sans Narrow (condensed)', preview: "'Arial Narrow', 'Helvetica Neue Condensed', Arial, sans-serif" },
  { value: 'DejaVu Serif', label: 'DejaVu Serif', preview: "'DejaVu Serif', Georgia, serif" },
  { value: 'Liberation Serif', label: 'Liberation Serif (≈ Times)', preview: "'Times New Roman', Times, serif" },
  { value: 'DejaVu Sans Mono', label: 'DejaVu Sans Mono', preview: "'DejaVu Sans Mono', Menlo, monospace" },
];
// Unknown families fall back to DejaVu Sans BECAUSE the server does
// (textOverlay.js resolveFontFamily) — falling back to the first option
// would preview a font the saved render won't use.
const DEJAVU_OPTION = FONT_OPTIONS.find(f => f.value === 'DejaVu Sans')!;
const previewFont = (family?: string) => (FONT_OPTIONS.find(f => f.value === family) || DEJAVU_OPTION).preview;

const DISPLAY_WIDTH = 540;

// The renderers put the top of a backing pill 0.78em above the first baseline
// and its bottom 0.22em below the last one (overlayCanvas.drawOverlay, and
// textOverlay.js on the server). A DOM block instead spans a full line box per
// line, so the pill is drawn as a pseudo-element offset off the block rather
// than as the block's own background — otherwise the preview sits 0.22em high
// and 0.2em tall against what actually gets saved.
// How small and large a corner-drag may take the type. Below ~8px it is
// unreadable at any preview scale; above ~400px one block owns the whole frame.
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 400;


interface OverlayEditorProps {
  open: boolean;
  row: GenerationRow;
  service: StaticAdsService;
  onClose: () => void;
  /** Called with the fresh output URL after a successful save. */
  onSaved: () => void;
}

function parseSpec(raw: GenerationRow['overlaySpec']): OverlaySpec | null {
  if (!raw) return null;
  const spec = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  return spec && Array.isArray(spec.elements) ? spec : null;
}

export default function OverlayEditor({ open, row, service, onClose, onSaved }: OverlayEditorProps) {
  const initial = useMemo(() => {
    const parsed = parseSpec(row.overlaySpec)?.elements || [];
    if (parsed.length) return parsed;
    // AI-text generations arrive with no spec — start with one block so
    // there's something to grab. (Their current output becomes the base.)
    return [{ lines: ['New text'], x: 60, y: 120, fontSize: 48, fontWeight: 700, color: '#FFFFFF', align: 'start' } as OverlayElement];
  }, [row.overlaySpec]);
  const [elements, setElements] = useState<OverlayElement[]>(() => JSON.parse(JSON.stringify(initial)));
  const [selected, setSelected] = useState<number>(0);
  const [naturalWidth, setNaturalWidth] = useState<number | null>(null);
  // Custom fonts uploaded by users: listed after the built-ins and previewed
  // for real via @font-face on their storage URL.
  const [customFonts, setCustomFonts] = useState<Array<{ fontId: string; family: string; url: string | null }>>([]);
  const [fontUploading, setFontUploading] = useState(false);
  const fontInput = useRef<HTMLInputElement | null>(null);
  const effectRefInput = useRef<HTMLInputElement | null>(null);
  const [effectRefUploading, setEffectRefUploading] = useState(false);

  const uploadEffectRef = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setEffectRefUploading(true);
    setError(null);
    try {
      const { key } = await service.uploadEffectRef(file);
      patchSelected({ effectRefKey: key });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Reference upload failed');
    } finally {
      setEffectRefUploading(false);
      if (effectRefInput.current) effectRefInput.current.value = '';
    }
  };
  const [saving, setSaving] = useState(false);
  const [savingStep, setSavingStep] = useState<'render' | 'effects'>('render');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Undo/redo: snapshots of the whole elements array. Rapid keystrokes
  // coalesce into one entry (600ms window); drags and add/remove always get
  // their own. Redo clears on any new edit, like every editor.
  const undoStack = useRef<OverlayElement[][]>([]);
  const redoStack = useRef<OverlayElement[][]>([]);
  const lastPushAt = useRef(0);
  const [historyVersion, setHistoryVersion] = useState(0);

  const snapshot = (coalesce: boolean) => {
    const now = Date.now();
    if (coalesce && now - lastPushAt.current < 600) return;
    setElements(prev => {
      undoStack.current.push(JSON.parse(JSON.stringify(prev)));
      if (undoStack.current.length > 100) undoStack.current.shift();
      return prev;
    });
    lastPushAt.current = now;
    redoStack.current = [];
    setHistoryVersion(v => v + 1);
  };

  const undo = () => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    setElements(current => {
      redoStack.current.push(JSON.parse(JSON.stringify(current)));
      return prev;
    });
    lastPushAt.current = 0;
    setSelected(0);
    setHistoryVersion(v => v + 1);
  };

  const redo = () => {
    const next = redoStack.current.pop();
    if (!next) return;
    setElements(current => {
      undoStack.current.push(JSON.parse(JSON.stringify(current)));
      return next;
    });
    lastPushAt.current = 0;
    setSelected(0);
    setHistoryVersion(v => v + 1);
  };

  // Copy/paste buffer for whole blocks (cmd+C / cmd+V outside text fields).
  const copiedBlock = useRef<OverlayElement | null>(null);

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Inside a text field every key belongs to the field (its own undo, its
    // own backspace, its own clipboard).
    const inField = /input|textarea/i.test((e.target as HTMLElement).tagName);
    // Deleting a block requires focus inside the preview stage — a Backspace
    // on any other dialog control (a select, a button) must not eat a block.
    const inStage = Boolean((e.target as HTMLElement).closest?.('[data-overlay-stage]'));

    if (!inField && inStage && (e.key === 'Backspace' || e.key === 'Delete')) {
      if (elements.length > 1) {
        e.preventDefault();
        snapshot(false);
        setElements(prev => prev.filter((_, i) => i !== selected));
        setSelected(0);
      }
      return;
    }

    if (!(e.metaKey || e.ctrlKey)) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey && !inField) { e.preventDefault(); undo(); }
    else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); }
    else if (key === 'c' && !inField) {
      const el2 = elements[selected];
      if (el2) { e.preventDefault(); copiedBlock.current = JSON.parse(JSON.stringify(el2)); }
    } else if (key === 'v' && !inField) {
      if (copiedBlock.current) {
        e.preventDefault();
        snapshot(false);
        const dupe: OverlayElement = {
          ...JSON.parse(JSON.stringify(copiedBlock.current)),
          x: (copiedBlock.current.x || 0) + 24,
          y: (copiedBlock.current.y || 0) + 24,
        };
        // A cached effect fragment is position-bound; the hash mismatch would
        // regenerate it anyway — drop it so the dupe renders cleanly.
        delete dupe.effectSvg;
        delete dupe.effectHash;
        setElements(prev => [...prev, dupe]);
        setSelected(elements.length);
      }
    }
  };

  // Re-seed local state when a different generation opens. Clearing the marker
  // on close makes Cancel actually discard: reopening the SAME generation
  // re-seeds from the saved spec instead of resurrecting the abandoned edits.
  const seededFor = useRef<string | null>(null);
  if (!open) seededFor.current = null;
  if (open && seededFor.current !== row.generationId) {
    seededFor.current = row.generationId;
    setElements(JSON.parse(JSON.stringify(initial)));
    setSelected(0);
    setError(null);
    undoStack.current = [];
    redoStack.current = [];
    lastPushAt.current = 0;
  }

  useEffect(() => {
    if (!open) return;
    void service.getFonts().then(resp => setCustomFonts(resp.custom || [])).catch(() => {});
  }, [open, service]);

  // Load the real overlay webfonts as soon as the editor opens; when they
  // resolve, bump fontsVersion so the preview re-renders (and later
  // measurements run) with real metrics instead of fallback faces.
  const [fontsVersion, setFontsVersion] = useState(0);
  useEffect(() => {
    if (!open) return;
    loadAllOverlayFonts();
    void loadOverlayFonts(elements, customFonts.map(f => f.family))
      .then(() => setFontsVersion(v => v + 1));
    // Reload on generation switch / custom-font arrival, not per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row.generationId, customFonts]);

  const uploadFont = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setFontUploading(true);
    setError(null);
    try {
      const { family } = await service.uploadFont(file);
      const resp = await service.getFonts();
      setCustomFonts(resp.custom || []);
      patchSelected({ fontFamily: family });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Font upload failed');
    } finally {
      setFontUploading(false);
      if (fontInput.current) fontInput.current.value = '';
    }
  };

  const scale = naturalWidth ? DISPLAY_WIDTH / naturalWidth : 1;

  // One DOM node per block, so save can MEASURE the real rendered text width
  // (÷ scale → image px). The server sizes backing pills from it — estimates
  // drifted visibly on centered text.
  const blockRefs = useRef<(HTMLElement | null)[]>([]);

  const drag = useRef<{ idx: number; startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null);
  // Corner-drag font sizing. Scale is measured from the block's CENTRE rather
  // than the opposite corner, so all four handles behave the same way and
  // "outward is bigger" holds however the block is aligned.
  const resize = useRef<{ idx: number; cx: number; cy: number; startDist: number; origFontSize: number; moved: boolean } | null>(null);

  const onPointerMove = (e: React.PointerEvent) => {
    if (resize.current) {
      const { idx, cx, cy, startDist, origFontSize } = resize.current;
      const dist = Math.hypot(e.clientX - cx, e.clientY - cy);
      if (!resize.current.moved) {
        if (Math.abs(dist - startDist) < 1) return;
        resize.current.moved = true;
        snapshot(false);
      }
      // startDist is measured at pointerdown and can be ~0 if the handle sits on
      // the centre of a tiny block; guard so the ratio cannot blow up.
      const factor = startDist > 4 ? dist / startDist : 1;
      const next = Math.round(Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, origFontSize * factor)));
      setElements(prev => prev.map((el, i) => (i === idx ? { ...el, fontSize: next } : el)));
      return;
    }
    if (!drag.current) return;
    const { idx, startX, startY, origX, origY } = drag.current;
    // The undo snapshot lands on the FIRST real movement, not on pointerdown:
    // a plain click-to-select must not add a no-op history entry.
    if (!drag.current.moved) {
      if (e.clientX === startX && e.clientY === startY) return;
      drag.current.moved = true;
      snapshot(false);
    }
    setElements(prev => prev.map((el, i) => (i === idx
      ? { ...el, x: Math.round(origX + (e.clientX - startX) / scale), y: Math.round(origY + (e.clientY - startY) / scale) }
      : el)));
  };

  const patchSelected = (patch: Partial<OverlayElement>) => {
    snapshot(true);
    setElements(prev => prev.map((el, i) => (i === selected ? { ...el, ...patch } : el)));
  };

  const el = elements[selected];

  const save = async () => {
    setSaving(true);
    setSavingStep('render');
    setError(null);
    setNotice(null);
    try {
      const base = elements.map((element) => {
        const out = { ...element };
        // A whitespace-only effect is no effect — normalize it away so nothing
        // downstream can mistake residue for an instruction and call the AI.
        if (!out.effect || !out.effect.trim()) {
          delete out.effect;
          delete out.effectRefKey;
        } else {
          out.effect = out.effect.trim();
        }
        return out;
      });

      // Only publish client pixels/measurements when every referenced font
      // face actually resolved — otherwise the canvas (and textWidth) would
      // bake in browser-fallback typefaces the server never uses.
      const fontsOk = await loadOverlayFonts(base, customFonts.map(f => f.family));
      let specElements = base;
      if (fontsOk) {
        const measured = base.map((element, i) => {
          const node = blockRefs.current[i];
          const width = node ? node.getBoundingClientRect().width / scale : null;
          return width && Number.isFinite(width) ? { ...element, textWidth: Math.round(width) } : element;
        });
        specElements = measured;

        // WYSIWYG: composite plate + text on a canvas with the SAME fonts the
        // preview used and upload those pixels. The server re-renders nothing.
        try {
          const plateBlob = await service.getPlateBlob(row.generationId);
          const bitmap = await createImageBitmap(plateBlob);
          let blob: Blob;
          try {
            blob = await renderOverlayToBlob(bitmap, measured, (family) => (
              customFonts.some(f => f.family === family)
                ? `'${family}', sans-serif`
                : previewFont(family)
            ));
          } finally {
            // Bitmaps hold plate-sized pixel buffers outside the JS heap —
            // release explicitly instead of waiting on GC.
            bitmap.close();
          }
          await service.uploadOverlayRender(row.generationId, blob, { elements: measured });
        } catch (canvasErr) {
          // Canvas can fail (old browser, blocked bitmap) — the server SVG
          // render is the safety net, with the same fonts guaranteed there.
          console.warn('Client render failed — falling back to server render', canvasErr);
          await service.updateOverlay(row.generationId, { elements: measured });
        }
      } else {
        // No client render and no client textWidth: the server renders with
        // guaranteed fonts and estimates widths itself. Strip any textWidth
        // persisted by an earlier save — it describes the OLD wording/fonts,
        // and the server prefers a present textWidth over its own estimate.
        const unmeasured = base.map((element) => {
          const out = { ...element };
          delete out.textWidth;
          return out;
        });
        await service.updateOverlay(row.generationId, { elements: unmeasured });
      }

      // Effects are the ONLY generative step, and they run AFTER the
      // deterministic render — an image pass over the finished composite.
      if (specElements.some(el => el.effect && el.effect.trim())) {
        setSavingStep('effects');
        try {
          await service.applyEffects(row.generationId);
        } catch (fxErr: any) {
          setError(`Effects failed: ${fxErr?.response?.data?.error || fxErr?.message || 'unknown error'} — the plain render was saved.`);
          onSaved();
          setSaving(false);
          return;
        }
      }

      onSaved();
      if (fontsOk) {
        onClose();
      } else {
        // Saved fine — but not with the pixels on screen. Say so instead of
        // silently closing as if the preview had been rendered verbatim.
        setNotice("Fonts didn't load in this browser — the image was rendered on the server instead.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth onKeyDown={onKeyDown}>
      <DialogTitle>
        Edit text overlay
        <Tooltip title="Undo (⌘Z / Ctrl+Z)">
          <span>
            <IconButton size="small" sx={{ ml: 1 }} disabled={!undoStack.current.length} onClick={undo}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo (⇧⌘Z / Ctrl+Y)">
          <span>
            <IconButton size="small" disabled={!redoStack.current.length} onClick={redo}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {/* Real previews for uploaded fonts, straight off their storage URLs. */}
        <style>
          {customFonts.filter(f => f.url).map(f =>
            `@font-face { font-family: '${f.family.replace(/'/g, '')}'; src: url('${f.url}'); }`).join('\n')}
        </style>
        {saving && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 1.5,
            bgcolor: 'rgba(255,255,255,0.75)',
          }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              {savingStep === 'effects' ? 'Applying effects (uses image credits)…' : 'Rendering the final image…'}
            </Typography>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Drag a block to move it; drag a corner handle to size the type. Click to select and edit. The preview is approximate — Save
          renders the final image on the server. The AI-generated plate itself is untouched.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box
            data-overlay-stage
            data-fonts-version={fontsVersion}
            tabIndex={-1}
            sx={{ position: 'relative', width: DISPLAY_WIDTH, flex: '0 0 auto', userSelect: 'none', overflow: 'hidden', borderRadius: 1 }}
            onPointerMove={onPointerMove}
            onPointerUp={() => { drag.current = null; resize.current = null; }}
            onPointerLeave={() => { drag.current = null; resize.current = null; }}
          >
            <Box
              component="img"
              src={row.plateImageUrl || row.outputImageUrl || ''}
              alt="Base image"
              draggable={false}
              onLoad={e => setNaturalWidth(e.currentTarget.naturalWidth)}
              sx={{ width: '100%', display: 'block', borderRadius: 1 }}
            />
            {naturalWidth && elements.map((element, i) => {
              const fontSize = element.fontSize * scale;
              const anchor = element.align || 'start';
              const bg = element.background;
              // The pill's geometry is baseline-relative, so it needs the same
              // line count and line height the renderers measure.
              const lineCount = renderableLines(element.lines).length;
              const lineRatio = element.lineHeight || 1.2;
              // The gradient's CSS angle depends on the block's aspect ratio
              // (server gradients stretch to the block bbox); the ref's box is
              // one render stale, which only matters for the first paint of a
              // diagonal — axis-aligned angles are aspect-independent.
              const blockNode = blockRefs.current[i];
              return (
                // eslint-disable-next-line react/no-array-index-key
                <Box
                  key={i}
                  ref={(node: HTMLElement | null) => { blockRefs.current[i] = node; }}
                  onPointerDown={(e) => {
                    setSelected(i);
                    drag.current = { idx: i, startX: e.clientX, startY: e.clientY, origX: element.x, origY: element.y, moved: false };
                    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
                  }}
                  sx={{
                    position: 'absolute',
                    left: element.x * scale,
                    top: (element.y - element.fontSize) * scale,
                    transform: anchor === 'middle' ? 'translateX(-50%)' : anchor === 'end' ? 'translateX(-100%)' : undefined,
                    cursor: 'move',
                    fontFamily: customFonts.some(f => f.family === element.fontFamily)
                      ? `'${element.fontFamily}', sans-serif`
                      : previewFont(element.fontFamily),
                    fontStyle: element.fontStyle === 'italic' ? 'italic' : 'normal',
                    fontSize,
                    fontWeight: element.fontWeight || 400,
                    lineHeight: element.lineHeight || 1.2,
                    color: element.color,
                    ...(element.gradient?.from && element.gradient?.to ? {
                      backgroundImage: `linear-gradient(${gradientCssAngle(element.gradient.angle ?? 90, blockNode?.offsetWidth || 1, blockNode?.offsetHeight || 1)}deg, ${element.gradient.from}, ${element.gradient.to})`,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    } : {}),
                    whiteSpace: 'pre',
                    textAlign: anchor === 'middle' ? 'center' : anchor === 'end' ? 'right' : 'left',
                    // Drawn as a pseudo-element rather than as this block's own
                    // background: the pill hangs off the BASELINES, while a DOM
                    // block spans whole line boxes. `isolation` keeps the
                    // negative z-index behind the text without letting it fall
                    // behind the plate image.
                    ...(bg?.color && lineCount ? (() => {
                      const pad = (bg.padding ?? 12) * scale;
                      return {
                        isolation: 'isolate',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          zIndex: -1,
                          left: -pad,
                          right: -pad,
                          top: (1 - inkExtentEm(renderableLines(element.lines)).ascent) * fontSize - pad,
                          bottom: (lineRatio - 1 - inkExtentEm(renderableLines(element.lines)).descent) * fontSize - pad,
                          bgcolor: colorWithOpacity(bg.color, bg.opacity ?? 1),
                          borderRadius: `${(bg.radius ?? 0) * scale}px`,
                        },
                      };
                    })() : {}),
                    outline: i === selected ? '1.5px dashed rgba(120,120,255,0.9)' : 'none',
                    outlineOffset: 4,
                  }}
                >
                  {/* Corner handles, on the selected block only. Dragging one
                      scales the FONT SIZE — the block has no width of its own to
                      resize, its box is whatever the type occupies. */}
                  {i === selected && ([
                    ['nw', { top: -5, left: -5, cursor: 'nwse-resize' }],
                    ['ne', { top: -5, right: -5, cursor: 'nesw-resize' }],
                    ['sw', { bottom: -5, left: -5, cursor: 'nesw-resize' }],
                    ['se', { bottom: -5, right: -5, cursor: 'nwse-resize' }],
                  ] as const).map(([corner, pos]) => (
                    <Box
                      key={corner}
                      onPointerDown={(e) => {
                        // Stop the block's own handler, or the block would start
                        // MOVING at the same time as it resizes.
                        e.stopPropagation();
                        const rect = blockRefs.current[i]?.getBoundingClientRect();
                        if (!rect) return;
                        const cx = rect.left + rect.width / 2;
                        const cy = rect.top + rect.height / 2;
                        resize.current = {
                          idx: i,
                          cx,
                          cy,
                          startDist: Math.hypot(e.clientX - cx, e.clientY - cy),
                          origFontSize: element.fontSize,
                          moved: false,
                        };
                        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
                      }}
                      sx={{
                        position: 'absolute',
                        width: 10,
                        height: 10,
                        bgcolor: '#fff',
                        border: '1.5px solid rgba(120,120,255,0.95)',
                        borderRadius: '2px',
                        zIndex: 2,
                        ...pos,
                      }}
                    />
                  ))}
                  {renderableLines(element.lines).map((line, li) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={li}>
                      {li > 0 && '\n'}
                      {parseInlineMarkup(line).map((seg, si) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <span
                          key={si}
                          style={{
                            fontWeight: seg.bold ? Math.min(900, Math.max(700, (element.fontWeight || 400) + 100)) : undefined,
                            fontStyle: seg.italic ? 'italic' : undefined,
                          }}
                        >
                          {seg.text}
                        </span>
                      ))}
                    </React.Fragment>
                  ))}
                  {element.effect?.trim() && (
                    <Box component="span" sx={{
                      position: 'absolute', top: -18, right: -6, fontSize: 10, fontFamily: 'monospace',
                      bgcolor: 'warning.main', color: 'warning.contrastText', px: 0.5, borderRadius: 0.5,
                      WebkitTextFillColor: 'initial',
                    }}>
                      fx
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 240 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                select size="small" label="Block" value={Math.min(selected, elements.length - 1)}
                onChange={e => setSelected(Number(e.target.value))}
                sx={{ flex: 1 }}
              >
                {elements.map((element, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MenuItem key={i} value={i}>{(element.lines?.[0] || `Block ${i + 1}`).slice(0, 32)}</MenuItem>
                ))}
              </TextField>
              <IconButton
                size="small"
                onClick={() => {
                  snapshot(false);
                  setElements(prev => [...prev, {
                    lines: ['New text'], x: 60, y: 120, fontSize: 48, fontWeight: 700, color: '#FFFFFF', align: 'start',
                  }]);
                  setSelected(elements.length);
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={elements.length <= 1}
                onClick={() => {
                  snapshot(false);
                  setElements(prev => prev.filter((_, i) => i !== selected));
                  setSelected(0);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>

            {el && (
              <>
                <TextField
                  size="small" label="Text (one line per row)" multiline minRows={2} maxRows={6}
                  value={(el.lines || []).join('\n')}
                  onChange={e => patchSelected({ lines: e.target.value.split('\n') })}
                  helperText="Inline emphasis: **bold**, *italic*, ***both*** — flows seamlessly inside the line (no separate blocks needed)"
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    select size="small" label="Font" value={el.fontFamily || 'DejaVu Sans'}
                    onChange={e => patchSelected({ fontFamily: e.target.value })}
                    sx={{ flex: 1 }}
                  >
                    {FONT_OPTIONS.map(f => (
                      <MenuItem key={f.value} value={f.value} sx={{ fontFamily: f.preview }}>{f.label}</MenuItem>
                    ))}
                    {customFonts.map(f => (
                      <MenuItem key={f.fontId} value={f.family} sx={{ fontFamily: `'${f.family}', sans-serif` }}>
                        {f.family} (custom)
                      </MenuItem>
                    ))}
                  </TextField>
                  <input
                    ref={fontInput} type="file" accept=".ttf,.otf" hidden
                    onChange={e => uploadFont(e.target.files)}
                  />
                  <Tooltip title="Upload a .ttf/.otf — its real family name is read from the file; it becomes available everywhere, server render included">
                    <span>
                      <Button size="small" disabled={fontUploading} onClick={() => fontInput.current?.click()}>
                        {fontUploading ? <CircularProgress size={14} /> : 'Add font'}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small" label="Size" type="number" value={el.fontSize}
                    onChange={e => patchSelected({ fontSize: Number(e.target.value) || 12 })}
                    sx={{ width: 90 }}
                  />
                  <TextField
                    select size="small" label="Weight" value={el.fontWeight || 400}
                    onChange={e => patchSelected({ fontWeight: Number(e.target.value) })}
                    sx={{ width: 110 }}
                  >
                    {[400, 600, 700, 800].map(w => <MenuItem key={w} value={w}>{w}</MenuItem>)}
                  </TextField>
                  <Tooltip title="Italic">
                    <Button
                      size="small"
                      variant={el.fontStyle === 'italic' ? 'contained' : 'outlined'}
                      onClick={() => patchSelected({ fontStyle: el.fontStyle === 'italic' ? 'normal' : 'italic' })}
                      sx={{ minWidth: 36, fontStyle: 'italic', fontWeight: 700 }}
                    >
                      I
                    </Button>
                  </Tooltip>
                  <TextField
                    size="small" label="Color" type="color" value={el.color || '#ffffff'}
                    onChange={e => patchSelected({ color: e.target.value })}
                    sx={{ width: 90 }}
                  />
                </Stack>
                <TextField
                  select size="small" label="Align on image" value={el.align || 'start'}
                  onChange={e => {
                    // Alignment places the block ON THE IMAGE: left margin,
                    // horizontal center, or right margin — visibly, not just a
                    // change of anchor semantics.
                    const next = e.target.value as OverlayElement['align'];
                    if (!naturalWidth) { patchSelected({ align: next }); return; }
                    const margin = Math.round(naturalWidth * 0.04);
                    const x = next === 'middle' ? Math.round(naturalWidth / 2)
                      : next === 'end' ? naturalWidth - margin : margin;
                    patchSelected({ align: next, x });
                  }}
                >
                  <MenuItem value="start">Left edge</MenuItem>
                  <MenuItem value="middle">Centered</MenuItem>
                  <MenuItem value="end">Right edge</MenuItem>
                </TextField>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={Boolean(el.gradient?.from)}
                        onChange={e => patchSelected({
                          gradient: e.target.checked ? { from: '#FFFFFF', to: '#4A90D9', angle: 90 } : null,
                        })}
                      />
                    )}
                    label={<Typography variant="body2">Gradient fill</Typography>}
                    sx={{ mr: 0 }}
                  />
                  {el.gradient?.from && (
                    <>
                      <TextField
                        size="small" label="From" type="color" value={el.gradient.from}
                        onChange={e => patchSelected({ gradient: { ...el.gradient!, from: e.target.value } })}
                        sx={{ width: 80 }}
                      />
                      <TextField
                        size="small" label="To" type="color" value={el.gradient.to}
                        onChange={e => patchSelected({ gradient: { ...el.gradient!, to: e.target.value } })}
                        sx={{ width: 80 }}
                      />
                      <TextField
                        select size="small" label="Direction" value={el.gradient.angle ?? 90}
                        onChange={e => patchSelected({ gradient: { ...el.gradient!, angle: Number(e.target.value) } })}
                        sx={{ width: 130 }}
                      >
                        <MenuItem value={90}>Top to bottom</MenuItem>
                        <MenuItem value={0}>Left to right</MenuItem>
                        <MenuItem value={45}>Diagonal</MenuItem>
                        <MenuItem value={270}>Bottom to top</MenuItem>
                      </TextField>
                    </>
                  )}
                </Stack>

                <TextField
                  size="small" label="Special effect (optional)"
                  placeholder="e.g. chrome outline with a soft drop shadow"
                  value={el.effect || ''}
                  onChange={e => patchSelected({ effect: e.target.value || null })}
                  helperText="Applied AFTER the exact render, as an AI image pass over the finished composite (uses image credits). The preview shows base styling; the fx badge marks the block."
                />
                {el.effect?.trim() && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <input
                      ref={effectRefInput} type="file" accept="image/*" hidden
                      onChange={e => uploadEffectRef(e.target.files)}
                    />
                    <Tooltip title="Upload an image whose lettering style the effect should imitate — the AI sees it alongside the plate">
                      <span>
                        <Button size="small" disabled={effectRefUploading} onClick={() => effectRefInput.current?.click()}>
                          {effectRefUploading ? <CircularProgress size={14} /> : (el.effectRefKey ? 'Replace style reference' : 'Add style reference')}
                        </Button>
                      </span>
                    </Tooltip>
                    {el.effectRefKey && (
                      <Typography
                        variant="caption" color="text.secondary"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => patchSelected({ effectRefKey: null })}
                      >
                        reference attached — remove
                      </Typography>
                    )}
                  </Stack>
                )}

                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={Boolean(el.background?.color)}
                        onChange={e => patchSelected({
                          background: e.target.checked
                            ? { color: '#000000', opacity: 0.4, padding: 14, radius: 8 }
                            : null,
                        })}
                      />
                    )}
                    label={<Typography variant="body2">Backing pill</Typography>}
                    sx={{ mr: 0 }}
                  />
                  {el.background?.color && (
                    <>
                      <TextField
                        size="small" label="Color" type="color"
                        value={el.background.color}
                        onChange={e => patchSelected({ background: { ...el.background!, color: e.target.value } })}
                        sx={{ width: 90 }}
                      />
                      <TextField
                        size="small" label="Opacity" type="number"
                        inputProps={{ step: 0.05, min: 0, max: 1 }}
                        value={el.background.opacity ?? 1}
                        onChange={e => patchSelected({
                          background: { ...el.background!, opacity: Math.max(0, Math.min(1, Number(e.target.value))) },
                        })}
                        helperText="0 = fully transparent"
                        sx={{ width: 110 }}
                      />
                    </>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {notice && <Alert severity="info" sx={{ mt: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !elements.length}>
          {saving
            ? (savingStep === 'effects' ? 'Applying effects…' : 'Rendering…')
            : (elements.some(el => el.effect && el.effect.trim()) ? 'Save + apply effects' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
